import TabContent from 'pages/common/tabContent';
import React, { useMemo, useState } from 'react';
import { ShortcutsToolbar } from './shortcutsToolbar';
import { useDispatch, useSelector } from 'react-redux';
import { Shortcut, ShortcutAction, ShortcutGroup, ShortcutGroupAction, ShortcutGroupToShortcuts, ShortcutUpdateModelv1, ShortcutsFilterv1, defaultShortcutsFilter } from '../models';
import { ShortcutsFilter } from './shortcutsFilter';
import { ShortcutsList } from './shortcutsList/shortcutsList';
import { GlobalApplicationState } from 'globalApplicationState';
import EditShortcutDialog from './dialogs/editShorcutDialog';
import EditShortcutGroupDialog from './dialogs/editShortcutGroupDialog';
import MoveShortcutsDialog from './dialogs/moveShortcutsDialog';
import { Audience } from 'modules/audiences';
import DeleteShortcutsDialog from './dialogs/deleteShortcutsDialog';
import { bulkDeleteShortcutsAndGroups, bulkUpdateShortcuts, updateShortcut, updateShortcutGroup } from '../reducer';
import ShallowEquals from 'utils/shallowEquals';

interface IShortcutsListingProps {
    onCreateShortcut: (shortcutGroup?: ShortcutGroup) => void;
    filteredShortcutFormat: ShortcutGroupToShortcuts[];
    audiences: Audience[];
    onFilterChange: (filter: ShortcutsFilterv1) => void;
    expandedShortcutGroups: string[];
    setExpandedShortcutGroups: React.Dispatch<React.SetStateAction<string[]>>;
    shortcutsFilter: ShortcutsFilterv1;
}

const ShortcutsListing: React.FC<IShortcutsListingProps> = ({
    onCreateShortcut,
    audiences,
    filteredShortcutFormat,
    onFilterChange,
    expandedShortcutGroups,
    setExpandedShortcutGroups,
    shortcutsFilter
}) => {
    const [selectedShortcuts, setSelectedShortcuts] = useState<Shortcut[]>([]);
    const [selectedShortcutGroup, setSelectedShortcutGroup] = useState<ShortcutGroup | undefined>(undefined);
    const [selectedSingleShortcut, setSelectedSingleShortcut] = useState<Shortcut | undefined>(undefined);
    const [selectedShortcutGroups, setSelectedShortcutGroups] = useState<ShortcutGroup[]>([]);
    const [selectedShortcutsToDelete, setSelectedShortcutsToDelete] = useState<Shortcut[]>([]);
    const [hiddenSelectedShortcuts, setHiddenSelectedShortcuts] = useState<Shortcut[]>([]);
    const [showEditShortcutDialog, setShowEditShortcutDialog] = useState(false);
    const [showEditShortcutGroupDialog, setShowEditShortcutGroupDialog] = useState(false);
    const [showMoveShortcutsDialog, setShowMoveShortcutsDialog] = useState(false);
    const [showDeleteShortcutsDialog, setShowDeleteShorcutsDialog] = useState(false);
    const dispatch = useDispatch();

    const hasFiltersApplied = useMemo(
        () => {
            const filterStateIsEqual = (): boolean => shortcutsFilter.state.every(state => defaultShortcutsFilter.state.includes(state));
            const filterAudienceIsEqual = (): boolean => shortcutsFilter.audiences.every(audience => defaultShortcutsFilter.audiences.includes(audience));
            return !ShallowEquals(shortcutsFilter.searchText, defaultShortcutsFilter.searchText) ||
                !filterStateIsEqual() ||
                !filterAudienceIsEqual();
        }, 
        [shortcutsFilter, defaultShortcutsFilter]
    );

    const {
        isFetching,
        isUpdating,
        originalShortcutFormat
    } = useSelector((state: GlobalApplicationState) => state.shortcuts);
    
    const onDeleteSelectedItems = (shortcutsToDelete: Shortcut[], shortcutGroupsToDelete: ShortcutGroup[]) => {
        deleteShortcutsAndShortcutGroups(shortcutsToDelete, shortcutGroupsToDelete);
    }

    const deleteShortcutsAndShortcutGroups = (shortcutsToDelete: Shortcut[], shortcutGroupsToDelete: ShortcutGroup[]) => {
        setShowDeleteShorcutsDialog(false);
        dispatch(bulkDeleteShortcutsAndGroups({ 
            shortcutIds: shortcutsToDelete.map(s => s.id), 
            shortcutGroupIds: shortcutGroupsToDelete.map(sg => sg.id), 
            filter: shortcutsFilter,
            successMsg: "Successfully deleted."
        }));
        resetSelectionState();
    }
    
    const onToolbarEditSelectedShortcut = () => {
        if (selectedShortcuts.length === 1) {
            setSelectedSingleShortcut(selectedShortcuts[0]);
            setShowEditShortcutDialog(true);
        }
    }

    const onToolbarDeleteSelectedShortcuts = () => {
        // if they delete a shortcut group, make sure the shortcuts within the group are also automatically selected to be deleted
        const selectedShortcutGroupIds = selectedShortcutGroups.map(sg => sg.id);
        // we use the original shortcut format instead of the filtered one because the filtered one may not have all the shortcuts listed
        const selectedShortcutGroupsToShorcuts = originalShortcutFormat.filter(f => selectedShortcutGroupIds.includes(f.shortcutGroup.id));
        const allSelectedShortcutsFromSelectedGroups = selectedShortcutGroupsToShorcuts.flatMap(sgts => sgts.shortcuts);
        const allSelectedShortcuts = [...selectedShortcuts, ...allSelectedShortcutsFromSelectedGroups.filter(s => !selectedShortcuts.find(ss => ss.id === s.id))];
        
        if (hasFiltersApplied) {
            const filteredSelectedShortcutsFromSelectedGroups = filteredShortcutFormat.filter(f => selectedShortcutGroupIds.includes(f.shortcutGroup.id)).flatMap(sgts => sgts.shortcuts);
            // these shortcuts are the ones added to the delete selection that are hidden because the filtered shortcuts format doesn't show them
            const hiddenSelectedShortcuts = allSelectedShortcutsFromSelectedGroups.filter(s => !filteredSelectedShortcutsFromSelectedGroups.find(ss => ss.id === s.id));
            setHiddenSelectedShortcuts(hiddenSelectedShortcuts);
        }

        setSelectedShortcuts(allSelectedShortcuts);
        setSelectedShortcutsToDelete(allSelectedShortcuts);
        setShowDeleteShorcutsDialog(true)
    }

    const onEditSelectedShortcutGroup = (newShortcutGroupName: string) => {
        updateSelectShortcutGroupName(selectedShortcutGroup!, newShortcutGroupName);
    }

    const onEditSelectedShortcut = (input: ShortcutUpdateModelv1) => {
        updateSelectedShortcut(input);
    }

    const updateSelectedShortcut = async (input: ShortcutUpdateModelv1) => {
        setShowEditShortcutDialog(false);
        dispatch(updateShortcut({
            shortcutId: selectedSingleShortcut?.id!, 
            shortcutUpdateModel: input, 
            filter: shortcutsFilter, 
            successMsg: "Successfully saved changes."
        }));
        resetSelectionState();
    }

    const updateSelectShortcutGroupName = async (shortcutGroup: ShortcutGroup, newName: string) => {
        setShowEditShortcutGroupDialog(false);
        dispatch(updateShortcutGroup({
            shortcutGroupId: shortcutGroup.id, 
            name: newName, 
            filter: shortcutsFilter, 
            successMsg: "Successfully updated shortcut group." 
        }));
        resetSelectionState();
    }

    const onMoveSelectedItems = (selectedGroupId: string, selectedShortcutsToMove: Shortcut[]) => {
        moveShortcutsToNewGroup(selectedShortcutsToMove, selectedGroupId);
    }

    const moveShortcutsToNewGroup = async (shortcutsToMove: Shortcut[], selectedGroupId: string) => {
        setShowMoveShortcutsDialog(false);
        const shortcutToMoveIds = shortcutsToMove.map(s => s.id);
        dispatch(bulkUpdateShortcuts({ 
            shortcutIds: shortcutsToMove.map(s => s.id), 
            groupIdToMoveTo: selectedGroupId, 
            scrollToId: shortcutToMoveIds[shortcutToMoveIds.length-1],
            successMsg: "Successfully moved to new shortcut group.",
            filter: shortcutsFilter
        }));
        resetSelectionState();
    }

    const onChangeStateOfSelectedShortcuts = (newState: boolean) => {
        changeStateOfShortcuts(selectedShortcuts, newState);
    }

    const changeStateOfShortcuts = async (shortcutsToChange: Shortcut[], newState: boolean) => {
        dispatch(bulkUpdateShortcuts({ 
            shortcutIds: shortcutsToChange.map(s => s.id), 
            setIsEnabled: newState,
            successMsg: `Successfully ${newState ? "enabled" : "disabled" } ${shortcutsToChange.length > 1 ? "selected shortcuts" : "shortcut"}.`,
            filter: shortcutsFilter
        }));
        resetSelectionState();
    }

    const resetSelectionState = () => {
        setSelectedShortcuts([]);
        setSelectedShortcutGroups([]);
        setSelectedShortcutGroup(undefined);
        setSelectedSingleShortcut(undefined);
        setSelectedShortcutsToDelete([]);
        onFilterChange(shortcutsFilter);
    }

    // for actions on a single shortcut
    const onShortcutAction = (shortcut: Shortcut, shortcutAction: ShortcutAction) => {
        switch (shortcutAction) {
            case ShortcutAction.EDIT:
                setSelectedSingleShortcut(shortcut);
                setShowEditShortcutDialog(true);
                break;
            case ShortcutAction.DELETE:
                setSelectedSingleShortcut(shortcut);
                setShowDeleteShorcutsDialog(true);
                break;
            case ShortcutAction.DISABLE:
                changeStateOfShortcuts([shortcut], false);
                break;
            case ShortcutAction.ENABLE:
                changeStateOfShortcuts([shortcut], true);
                break;
            case ShortcutAction.MOVE:
                setSelectedSingleShortcut(shortcut);
                setShowMoveShortcutsDialog(true);
                break;
        }
    }

    // for actions on a single shortcut group
    const onShortcutGroupAction = (shortcutGroup: ShortcutGroup, shortcutGroupAction: ShortcutGroupAction) => {
        const shortcutsOfShortcutGroup = filteredShortcutFormat.find(sgts => sgts.shortcutGroup.id === shortcutGroup.id)?.shortcuts ?? [];

        switch (shortcutGroupAction){
            case ShortcutGroupAction.ADD_SHORTCUT:
                onCreateShortcut(shortcutGroup);
                break;
            case ShortcutGroupAction.EDIT:
                setSelectedShortcutGroup(shortcutGroup);
                setShowEditShortcutGroupDialog(true);
                break;
            case ShortcutGroupAction.DELETE:
                setSelectedShortcutGroup(shortcutGroup);
                setSelectedShortcutsToDelete(shortcutsOfShortcutGroup);
                setShowDeleteShorcutsDialog(true);
                break;
            case ShortcutGroupAction.DISABLE_ALL:
                changeStateOfShortcuts(shortcutsOfShortcutGroup, false);
                break;
            case ShortcutGroupAction.ENABLE_ALL:
                changeStateOfShortcuts(shortcutsOfShortcutGroup, true);
                break;
        }
    }

    const getToolbar = (): JSX.Element => 
        <ShortcutsToolbar 
            selectedShortcuts={selectedShortcuts}
            selectedShortcutGroups={selectedShortcutGroups}
            onClearSelectedShortcuts={resetSelectionState}
            onDeleteSelectedShortcuts={onToolbarDeleteSelectedShortcuts}
            onEditSelectedShortcut={onToolbarEditSelectedShortcut}
            onMoveSelectedShortcuts={() => setShowMoveShortcutsDialog(true)}
            onSelectedShortcutsStateChange={onChangeStateOfSelectedShortcuts}
        />

    const getFilter = (): JSX.Element =>
        <ShortcutsFilter
            availableAudienceIdNames={audiences.map(a =>({ id: a.id, name: a.displayName }))}
            onFilterChange={onFilterChange}
            shortcutsFilter={shortcutsFilter}
            hasFiltersApplied={hasFiltersApplied}
        />;

    const getTableContents = (): JSX.Element => 
        <ShortcutsList 
            filteredShortcutFormat={filteredShortcutFormat}
            selectedShortcuts={selectedShortcuts}
            selectedShortcutGroups={selectedShortcutGroups}
            onChangeSelectedShortcuts={s => setSelectedShortcuts(s)}
            onChangeSelectedShortcutGroups={sg => setSelectedShortcutGroups(sg)}
            onShortcutAction={onShortcutAction}
            onShortcutGroupAction={onShortcutGroupAction}
            isFetching={isFetching}
            isUpdating={isUpdating}
            expandedShortcutGroups={expandedShortcutGroups}
            setExpandedShortcutGroups={setExpandedShortcutGroups}
            hasFiltersApplied={hasFiltersApplied}
        />

    const getDialogs = (): JSX.Element => 
        <React.Fragment>
            {
                selectedSingleShortcut &&
                <EditShortcutDialog 
                    show={showEditShortcutDialog}
                    onClose={() => {
                        setSelectedSingleShortcut(undefined); 
                        setShowEditShortcutDialog(false);
                    }}
                    onUpdateShortcutFromInput={onEditSelectedShortcut}
                    audiences={audiences}
                    shortcut={selectedSingleShortcut}
                    shortcutGroupId={filteredShortcutFormat.find(f => !!f.shortcuts.find(s => s.id === selectedSingleShortcut.id))!.shortcutGroup.id}
                />
            }
            {
                selectedShortcutGroup && 
                <EditShortcutGroupDialog 
                    show={showEditShortcutGroupDialog}
                    onClose={() => {
                        setSelectedShortcutGroup(undefined);
                        setShowEditShortcutGroupDialog(false);
                    }}
                    onEditShortcutGroupFromInput={onEditSelectedShortcutGroup}
                    shortcutGroupName={selectedShortcutGroup.name}
                />
            } 
            {
                (selectedShortcuts.length > 0 || selectedSingleShortcut) &&
                <MoveShortcutsDialog 
                    show={showMoveShortcutsDialog}
                    onClose={() => {
                        setShowMoveShortcutsDialog(false);

                        if (selectedSingleShortcut) 
                            setSelectedSingleShortcut(undefined);
                    }}
                    onMoveSelectedShortcuts={onMoveSelectedItems}
                    selectedShortcuts={selectedSingleShortcut ? [selectedSingleShortcut] : selectedShortcuts}
                />
            }
            <DeleteShortcutsDialog
                show={showDeleteShortcutsDialog}
                onClose={() => {
                    setShowDeleteShorcutsDialog(false)

                    if (selectedShortcutGroup) 
                        setSelectedShortcutGroup(undefined);

                    if (selectedSingleShortcut) 
                        setSelectedSingleShortcut(undefined);

                    // there's a special case where on a filtered shortcut format, if a user wants to delete a shortcut group, 
                    // all shortcuts within that group (even if it doesn't show in the filtered shortcut format) should be selected to be deleted
                    // however, if they exit out of the delete dialog, then we will automatically de-select those shortcuts that were automatically selected / not 
                    // visible in the filtered shortcut format
                    if (hasFiltersApplied) {
                        // get only the selected shortcuts that we see -- in the filtered shortcut format
                        // const selectedShortcutsInFilteredFormat = selectedShortcuts.filter(s => filteredShortcutFormat.flatMap(f => f.shortcuts).find(ss => ss.id === s.id));
                        const selectedShortcutsInFilteredFormat = selectedShortcuts.filter(s => !hiddenSelectedShortcuts.find(ss => ss.id === s.id));

                        // reset our selections to just what we see
                        setSelectedShortcuts([...selectedShortcutsInFilteredFormat]);
                        setSelectedShortcutsToDelete([...selectedShortcutsInFilteredFormat]);
                        setHiddenSelectedShortcuts([]);
                    }
                }}
                onDeleteSelectedShortcuts={onDeleteSelectedItems}
                shortcutsToDelete={selectedSingleShortcut ? [selectedSingleShortcut] : selectedShortcutsToDelete}
                shortcutGroupsToDelete={selectedSingleShortcut ? [] : selectedShortcutGroup ? [selectedShortcutGroup] : selectedShortcutGroups}
            />
        </React.Fragment>

    return <TabContent>
        {getToolbar()}
        {getFilter()}
        {getTableContents()}
        {getDialogs()}
    </TabContent>;
};

export default ShortcutsListing;